import { cloneDeep, isNull, isUndefined } from 'lodash';
import React, { useMemo, useState } from 'react';
import Switch from 'antd/es/switch';
import { Button, Input, Select, SliderInput, Title } from '@meitu/saas-ui';
import { ITextLayerProps, ITextMark } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@meitu/saas-editor-ui/es/components/label-action';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { TextMarkSvgUpload } from '@/editor/components/text-mark-svg-upload';
import { editor } from '@/instance';
import { useTextMarkModel } from '@/store/models/text-mark-model';
import { TextMarkStruct } from '@/types/text-mark';
import {
    getRepeatPathD,
    getSvgPathD,
    MarkPosition,
    MarKType,
    setTextMarkSvg,
    SizeRange,
} from './utils/text-mark-opera';
import './text-mark-panel.less';

export const TextMarkPanelKey = 'TEXT_MARK';

const { SimpleTitle } = Title;

const TextMarkPanel = () => {
    const textLayerId = useTextMarkModel((state) => state.textLayerId);
    const markConfig = useTextMarkModel((state) => state.markConfig);
    const setMarkConfig = useTextMarkModel((state) => state.setMarkConfig);
    const [sizeRangeIndex, setSizeRangeIndex] = useState<number>(1);

    const maxSize = useMemo(() => {
        if (!markConfig?.data?.sizeRange) return 100;
        switch (sizeRangeIndex) {
            case 0:
                return markConfig.data.sizeRange[1];
            case 1:
                return markConfig.data.sizeRange[2];
            default:
                return 100;
        }
    }, [markConfig?.data?.sizeRange, sizeRangeIndex]);

    const minSize = useMemo(() => {
        if (!markConfig?.data?.sizeRange) return 1;
        switch (sizeRangeIndex) {
            case 1:
                return markConfig.data.sizeRange[0];
            case 2:
                return markConfig.data.sizeRange[1];
            default:
                return 1;
        }
    }, [markConfig?.data?.sizeRange, sizeRangeIndex]);

    if (!markConfig) return null;

    // console.log({ textLayerId, markConfig });

    const handleChange = (
        key: keyof TextMarkStruct['data'],
        val: TextMarkStruct['data'][keyof TextMarkStruct['data']],
        sizeRangeIndex?: number,
    ) => {
        if (isUndefined(val) || isNull(val)) return;

        const newMarkConfig = {
            ...markConfig,
            data: {
                ...markConfig.data,
                [key]: val,
            },
        };

        // 如果类型是重复，则提取路径
        if (key === 'type' && val === 'repeat') {
            const pathD = getSvgPathD(newMarkConfig.data.src) ?? '';
            const repeatPathD = getRepeatPathD(pathD);

            if (repeatPathD) {
                newMarkConfig.data.repeatPath = repeatPathD;
            }
        }

        setMarkConfig(newMarkConfig);
        setTextMarkSvg(newMarkConfig);

        // 手动触发更新
        const textLayer = editor.layer.getLayer(textLayerId);

        if (textLayer) {
            const textMark = (textLayer.data as ITextLayerProps).textMark;
            const newTextMark = cloneDeep(textMark);

            if (!newTextMark) return;

            if (Object.keys(newTextMark).includes(key) || key === 'defaultColor' || key === 'sizeRange') {
                if (key === 'defaultColor') {
                    newTextMark.color = val as string;
                } else if (key === 'sizeRange' && !isUndefined(sizeRangeIndex)) {
                    newTextMark.size = SizeRange[sizeRangeIndex].value;
                } else {
                    newTextMark[key as keyof ITextMark] = val as never;
                }
            }
            editor.textLayer.setHighlight(textLayerId, newTextMark);
            editor.layer.forceUpdateLayer(textLayerId);
        }
    };

    const handleChangeUseBlackText = (val: boolean) => {
        const newMarkConfig = {
            ...markConfig,
            useBlackText: +val,
        };
        setMarkConfig(newMarkConfig);
        setTextMarkSvg(newMarkConfig);
    };

    return (
        <div className="p-text-mark-panel">
            <div className="p-text-mark-panel__title">
                <SimpleTitle text="划重点" fontSize={16} />
                <Button
                    type="ghost"
                    size="small"
                    onClick={() =>
                        TextMarkSvgUpload.show(
                            'textMark',
                            textLayerId,
                            async (markItem) => {
                                setMarkConfig(markItem);
                                setTextMarkSvg(markItem);
                                const textMark = {
                                    materialId: markItem.materialId,
                                    color: markItem.data.defaultColor,
                                    size: 50,
                                    enableSize: markItem.data.enableSize,
                                };
                                editor.textLayer.setHighlight(textLayerId, textMark);
                                editor.layer.forceUpdateLayer(textLayerId);
                            },
                            true,
                        )
                    }
                >
                    替换素材
                </Button>
            </div>

            <div className="text-mark-panel__content">
                <LabelAction title={'素材类型'} align={true}>
                    <Select
                        value={markConfig.data.type}
                        onChange={(val: string) => {
                            handleChange('type', val);
                        }}
                        style={{ width: 154 }}
                    >
                        {MarKType.map((item) => (
                            <Select.Option key={item.value} value={item.value}>
                                {item.label}
                            </Select.Option>
                        ))}
                    </Select>
                </LabelAction>

                {markConfig.data.type === 'repeat' && (
                    <LabelAction title={'重复路径'} align={true}>
                        <Input
                            style={{ width: 154 }}
                            value={markConfig.data.repeatPath}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                handleChange('repeatPath', e.target.value);
                            }}
                        />
                    </LabelAction>
                )}

                <LabelAction title={'对齐方式'} align={true}>
                    <Select
                        value={markConfig.data.position}
                        onChange={(val: string) => {
                            handleChange('position', val);
                        }}
                        style={{ width: 154 }}
                    >
                        {MarkPosition.map((item) => (
                            <Select.Option key={item.value} value={item.value}>
                                {item.label}
                            </Select.Option>
                        ))}
                    </Select>
                </LabelAction>

                <LabelAction title={'默认颜色'} align={true}>
                    <div style={{ width: 70 }}>
                        <ColorGroup
                            // showIcon={false}
                            color={markConfig.data.defaultColor}
                            setColor={(val: string) => {
                                handleChange('defaultColor', val);
                            }}
                            isGradient={false}
                        />
                    </div>
                </LabelAction>

                <LabelAction title={'粗细可调整'} align={true}>
                    <Switch
                        checked={markConfig.data.enableSize}
                        onChange={(val: boolean) => {
                            handleChange('enableSize', val);
                        }}
                    />
                </LabelAction>

                {markConfig.data.enableSize && (
                    <div className="size-range-container">
                        <Select
                            value={sizeRangeIndex}
                            onChange={(val: number) => {
                                setSizeRangeIndex(val);
                                handleChange('sizeRange', markConfig.data.sizeRange, val);
                            }}
                            style={{ width: 60 }}
                        >
                            {SizeRange.map((item, index) => (
                                <Select.Option key={item.value} value={index} label={item.label}>
                                    {item.label}
                                </Select.Option>
                            ))}
                        </Select>

                        <div style={{ width: 154 }}>
                            <SliderInput
                                value={markConfig.data.sizeRange[sizeRangeIndex]}
                                max={maxSize}
                                min={minSize}
                                showStepBtn={true}
                                type="horizontal"
                                onChange={(val: number) => {
                                    const sizeRange = [...markConfig.data.sizeRange];
                                    sizeRange[sizeRangeIndex] = val;
                                    handleChange('sizeRange', sizeRange, sizeRangeIndex);
                                }}
                            />
                        </div>
                    </div>
                )}

                <LabelAction title={'透明度调整'} align={true}>
                    <Switch
                        checked={markConfig.data.enableOpacity}
                        onChange={(val: boolean) => {
                            handleChange('enableOpacity', val);
                        }}
                    />
                </LabelAction>

                <LabelAction title={'左右填充'} align={true}>
                    <div style={{ width: 154 }}>
                        <SliderInput
                            value={markConfig.data.padding.x}
                            max={50}
                            min={-50}
                            showStepBtn={true}
                            type="horizontal"
                            onChange={(val: number) => {
                                handleChange('padding', {
                                    ...markConfig.data.padding,
                                    x: val,
                                });
                            }}
                        />
                    </div>
                </LabelAction>
                <LabelAction title={'上下填充'} align={true}>
                    <div style={{ width: 154 }}>
                        <SliderInput
                            value={markConfig.data.padding.y}
                            max={50}
                            min={-50}
                            showStepBtn={true}
                            type="horizontal"
                            onChange={(val: number) => {
                                handleChange('padding', {
                                    ...markConfig.data.padding,
                                    y: val,
                                });
                            }}
                        />
                    </div>
                </LabelAction>

                <LabelAction title={'左右偏移'} align={true}>
                    <div style={{ width: 154 }}>
                        <SliderInput
                            value={markConfig.data.offset.x}
                            max={50}
                            min={-50}
                            showStepBtn={true}
                            type="horizontal"
                            onChange={(val: number) => {
                                handleChange('offset', {
                                    ...markConfig.data.offset,
                                    x: val,
                                });
                            }}
                        />
                    </div>
                </LabelAction>
                <LabelAction title={'上下偏移'} align={true}>
                    <div style={{ width: 154 }}>
                        <SliderInput
                            value={markConfig.data.offset.y}
                            max={50}
                            min={-50}
                            showStepBtn={true}
                            type="horizontal"
                            onChange={(val: number) => {
                                handleChange('offset', {
                                    ...markConfig.data.offset,
                                    y: val,
                                });
                            }}
                        />
                    </div>
                </LabelAction>

                <LabelAction title={'缩略图强制黑色字'} align={true}>
                    <Switch
                        checked={!!markConfig.useBlackText}
                        onChange={(val: boolean) => {
                            handleChangeUseBlackText(val);
                        }}
                    />
                </LabelAction>

                <div className="panel-divider" />

                <div className="text-title">
                    <SimpleTitle text="Web 专属参数" fontSize={14} />
                </div>

                {markConfig.data.type === 'stretch' && (
                    <LabelAction title={'强制拉伸(会丢失圆角)'} align={true}>
                        <Switch
                            checked={markConfig.data.forceStretch}
                            onChange={(val: boolean) => {
                                handleChange('forceStretch', val);
                            }}
                        />
                    </LabelAction>
                )}

                <div className="panel-divider" />

                <div className="text-title">
                    <SimpleTitle text="底层专属参数" fontSize={14} />
                </div>
            </div>
        </div>
    );
};

export default React.memo(TextMarkPanel);
